import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Building2, Edit2, KeyRound, Search, Users, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../Common/context/ToastContext';
import SingleCpPharmacyManage from './SingleCpPharmacyManage';
import { KG_INICIS_BANK_LIST } from './bankList';
import SingleCpPayoutPanel, {
  normalizePayoutCapability,
  normalizePayoutProfile,
} from './SingleCpPayoutPanel';
import { ADMIN_CP_PAYOUT_TENANT_ENABLED } from '../../Common/config/menuConfig';
import {
  getAdminCpDetail,
  getAdminCpList,
  getAdminCpTerminals,
  resetAdminCpTerminalPassword,
  updateAdminCp,
  updateAdminCpTerminal,
} from '../api/cp';
import { getCpPayoutCapabilities, getCpPayoutProfile } from '../api/cpPayout';

const emptyTerminalForm = { terminalId: '', name: '', phone: '', memo: '' };
const terminalPharmacySource = String(
  process.env.REACT_APP_TERMINAL_PHARMACY_SOURCE || 'DISABLED',
).trim().toUpperCase();
const terminalRegistrationNotice = terminalPharmacySource === 'SHINSUNG'
  ? '영업자 등록은 신성 동기화에서 처리됩니다.'
  : terminalPharmacySource === 'EXCEL'
    ? '영업자 등록은 Excel 업로드에서만 처리됩니다.'
    : '영업자 등록은 연동된 데이터 소스에서 처리됩니다.';

function rowsOf(data) {
  const rows = data?.rows ?? data?.list ?? data?.terminals ?? (Array.isArray(data) ? data : []);
  return Array.isArray(rows) ? rows : [];
}

function normalizeCp(row = {}) {
  const cpIdIsNumeric = String(row.cp_id ?? '').trim() !== '' && Number.isInteger(Number(row.cp_id));
  const rawPk = row.id ?? row.cpPkId ?? row.cp_pk_id ?? row.cpPk ?? row.cp_pk ?? (cpIdIsNumeric ? row.cp_id : null);
  const numericPk = Number(rawPk);
  const rawLoginId = row.cpId ?? row.cpLoginId ?? row.cp_login_id ?? row.cp_id ?? '';
  const loginIdIsPk = String(rawLoginId).trim() !== '' && Number.isInteger(Number(rawLoginId)) && Number(rawLoginId) === numericPk;
  const cpLoginId = loginIdIsPk ? '' : rawLoginId;
  return {
    ...row,
    id: Number.isInteger(numericPk) && numericPk > 0 ? numericPk : null,
    cpId: cpLoginId,
    name: row.name ?? '',
    owner: row.bossName ?? row.boss_name ?? row.owner ?? '',
    businessNo: row.businessNo ?? row.business_no ?? '',
    phone: row.phone ?? '',
    email: row.email ?? '',
    address: row.address ?? '',
    feeRate: row.feeRate ?? row.fee_rate ?? '',
    bankName: row.bankName ?? row.bank_name ?? '',
    bankCode: row.bankCode ?? row.bank_code ?? '',
    accountNumber: row.accountNumber ?? row.bankAccountNumber ?? row.bank_account_number ?? '',
    accountHolder: row.accountHolder ?? row.bankAccountHolder ?? row.bank_account_holder ?? '',
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
  };
}

function normalizeTerminal(row = {}, index = 0) {
  const id = row.id ?? row.terminalPkId ?? row.terminal_pk_id ?? row.terminal_id ?? index;
  return {
    ...row,
    id,
    code: row.code ?? row.terminalCode ?? row.terminal_code ?? '-',
    terminalId: row.terminalId ?? row.loginId ?? row.login_id ?? row.terminal_id ?? '',
    name: row.name ?? row.terminalName ?? row.terminal_name ?? '',
    phone: row.phone ?? '',
    memo: row.memo ?? '',
    customerCount: Number(row.customerCount ?? row.customer_count ?? row.pharmacyCount ?? row.pharmacy_count ?? 0),
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
  };
}

function createInitialPayoutProfile(cp) {
  const missingFields = [
    !cp.businessNo && 'businessNo',
    !cp.name && 'companyName',
    !cp.owner && 'ownerName',
    !cp.bankCode && 'bankCode',
    !cp.accountNumber && 'bankAccountNumber',
    !cp.accountHolder && 'bankAccountHolder',
    !cp.phone && 'phone',
    !cp.email && 'email',
    'representativeBirthDate',
  ].filter(Boolean);
  return normalizePayoutProfile({
    profile: {
      cpId: cp.id,
      cpLoginId: cp.cpId,
      cpName: cp.name,
      cpStatus: cp.status,
      registrationStatus: 'INCOMPLETE',
      registrationMessage: '아직 지급 프로필이 생성되지 않았습니다.',
      missingFields,
      isNew: true,
    },
  });
}

function formatBusinessNo(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 10 ? digits.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3') : (value || '-');
}

function formatPhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '-';
  if (digits.startsWith('02')) return digits.replace(/^(02)(\d{3,4})(\d{4})$/, '$1-$2-$3');
  return digits.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
}

function Detail({ label, value, mono = false, wide = false }) {
  return (
    <div className={`min-w-0 border-b border-[#f0f2f1] py-[15px] ${wide ? 'col-span-2' : ''}`}>
      <span className="mb-1.5 block text-[13px] font-medium text-[#8b95a1]">{label}</span>
      <span className={`block break-words text-[17px] font-bold leading-[1.45] text-[#191f28] ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
    </div>
  );
}

function Modal({ title, children, onClose, footer }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#eef1f4] px-6 py-4">
          <h2 className="text-[19px] font-bold text-[#191f28]">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-[#8b95a1]"><X size={21} /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">{children}</div>
        <div className="flex gap-2 border-t border-[#eef1f4] bg-[#f9fafb] px-6 py-4">{footer}</div>
      </div>
    </div>
  );
}

const inputClass = 'h-[42px] w-full rounded-lg border border-[#dfe3e8] bg-white px-3 text-[14px] text-[#191f28] outline-none focus:border-[var(--brand-primary)]';

const kgInicisBankOptions = KG_INICIS_BANK_LIST.map((item) => ({ value: item.code, label: item.name }));
const bankSelectStyles = {
  control: (base, state) => ({ ...base, minHeight: '42px', borderRadius: '8px', borderColor: state.isFocused ? 'var(--brand-primary)' : '#dfe3e8', boxShadow: 'none', fontSize: '14px' }),
  menu: (base) => ({ ...base, zIndex: 10001 }),
  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
};

export default function SingleCpManage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [cp, setCp] = useState(null);
  const [cpLoading, setCpLoading] = useState(true);
  const [terminals, setTerminals] = useState([]);
  const [terminalLoading, setTerminalLoading] = useState(false);
  const [terminalError, setTerminalError] = useState('');
  const [query, setQuery] = useState('');
  const [terminalModal, setTerminalModal] = useState(null);
  const [terminalForm, setTerminalForm] = useState(emptyTerminalForm);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [cpEditOpen, setCpEditOpen] = useState(false);
  const [cpEdit, setCpEdit] = useState({});
  const [cpPanelTab, setCpPanelTab] = useState('payout');
  const [saving, setSaving] = useState(false);
  const [payoutCapability, setPayoutCapability] = useState(null);
  const [payoutProfile, setPayoutProfile] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutError, setPayoutError] = useState('');
  const payoutTabEnabled = ADMIN_CP_PAYOUT_TENANT_ENABLED && payoutCapability?.enabled === true;

  const activeTab = requestedTab === 'pharmacies' ? 'pharmacies' : 'salespeople';

  const loadCp = useCallback(async () => {
    setCpLoading(true);
    try {
      const data = await getAdminCpList({ page: 1, limit: 1 });
      const first = rowsOf(data)[0];
      if (!first) throw new Error('등록된 가맹점 정보가 없습니다.');
      const normalizedFirst = normalizeCp(first);
      if (!normalizedFirst.id) throw new Error('CP 숫자 PK를 확인할 수 없습니다. 서버 응답의 id 필드를 확인해 주세요.');
      let detail = first;
      const id = normalizedFirst.id;
      try { const response = await getAdminCpDetail(id); detail = { ...first, ...(response?.cp ?? response ?? {}) }; } catch (_) {}
      setCp(normalizeCp(detail));
    } catch (error) {
      setCp(null);
      showToastRef.current(error?.message || '조직 정보를 불러오지 못했습니다.', 'error');
    } finally {
      setCpLoading(false);
    }
  }, []);

  const loadTerminals = useCallback(async () => {
    if (!cp?.id) return;
    setTerminalLoading(true);
    setTerminalError('');
    try {
      const data = await getAdminCpTerminals(cp.id, { page: 1, limit: 500 });
      setTerminals(rowsOf(data).map(normalizeTerminal));
    } catch (error) {
      setTerminals([]);
      setTerminalError(error?.message || '관리자용 영업자 API 연결이 필요합니다.');
    } finally {
      setTerminalLoading(false);
    }
  }, [cp?.id]);

  const loadPayoutProfile = useCallback(async () => {
    if (!ADMIN_CP_PAYOUT_TENANT_ENABLED || !cp?.id) {
      setPayoutCapability(null);
      setPayoutProfile(null);
      setPayoutError('');
      return null;
    }
    setPayoutLoading(true);
    setPayoutError('');
    try {
      const capabilityData = await getCpPayoutCapabilities();
      const nextCapability = normalizePayoutCapability(capabilityData);
      setPayoutCapability(nextCapability);
      if (!nextCapability.enabled) {
        setPayoutProfile(null);
        return null;
      }
      try {
        const profileData = await getCpPayoutProfile(cp.id);
        const nextProfile = normalizePayoutProfile(profileData);
        setPayoutProfile(nextProfile);
        return nextProfile;
      } catch (error) {
        if (Number(error?.code) === 4040) {
          const initialProfile = createInitialPayoutProfile(cp);
          setPayoutProfile(initialProfile);
          setPayoutError('');
          return initialProfile;
        }
        setPayoutProfile(null);
        setPayoutError(error?.message || 'KG이니시스 지급 프로필을 불러오지 못했습니다.');
        return null;
      }
    } catch (error) {
      setPayoutCapability(null);
      setPayoutProfile(null);
      setPayoutError(error?.message || 'KG이니시스 지급 기능 설정을 확인하지 못했습니다.');
      return null;
    } finally {
      setPayoutLoading(false);
    }
  }, [cp]);

  useEffect(() => { loadCp(); }, [loadCp]);
  useEffect(() => { loadTerminals(); }, [loadTerminals]);
  useEffect(() => { loadPayoutProfile(); }, [loadPayoutProfile]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return terminals;
    return terminals.filter((row) => [row.code, row.terminalId, row.name, row.phone].some((value) => String(value || '').toLowerCase().includes(keyword)));
  }, [query, terminals]);


  const openEdit = (target) => {
    setTerminalForm({ terminalId: target.terminalId, name: target.name, phone: target.phone, memo: target.memo });
    setTerminalModal({ mode: 'edit', target });
  };

  const saveTerminal = async () => {
    const name = terminalForm.name.trim();
    const phone = terminalForm.phone.replace(/\D/g, '');
    if (!name || !phone) {
      showToast('이름과 연락처를 확인해 주세요.', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateAdminCpTerminal(cp.id, terminalModal.target.id, { name, phone, memo: terminalForm.memo.trim() });
      showToast('영업자 정보가 수정되었습니다.');
      setTerminalModal(null);
      await loadTerminals();
    } catch (error) {
      showToast(error?.message || '영업자 정보를 저장하지 못했습니다.', 'error');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (target) => {
    const status = target.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await updateAdminCpTerminal(cp.id, target.id, { status });
      showToast(status === 'ACTIVE' ? '영업자가 활성화되었습니다.' : '영업자가 비활성화되었습니다.');
      await loadTerminals();
    } catch (error) { showToast(error?.message || '상태를 변경하지 못했습니다.', 'error'); }
  };

  const savePassword = async () => {
    if (!newPassword.trim()) return showToast('새 비밀번호를 입력해 주세요.', 'error');
    setSaving(true);
    try {
      await resetAdminCpTerminalPassword(cp.id, passwordTarget.id, newPassword.trim());
      showToast('영업자 비밀번호가 변경되었습니다.');
      setPasswordTarget(null);
      setNewPassword('');
    } catch (error) { showToast(error?.message || '비밀번호를 변경하지 못했습니다.', 'error'); }
    finally { setSaving(false); }
  };

  const openCpEdit = () => {
    const bankOption = kgInicisBankOptions.find((option) => option.value === cp.bankCode || option.label === cp.bankName);
    setCpEdit({ name: cp.name, owner: cp.owner, businessNo: cp.businessNo, phone: cp.phone, email: cp.email, address: cp.address, feeRate: cp.feeRate, bankName: bankOption?.label ?? '', bankCode: bankOption?.value ?? '', accountNumber: cp.accountNumber, accountHolder: cp.accountHolder });
    setCpEditOpen(true);
  };

  const saveCp = async () => {
    if (!cpEdit.bankCode || !cpEdit.bankName) {
      showToast('KG이니시스에서 지원하는 은행을 선택해 주세요.', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateAdminCp({ id: cp.id, name: cpEdit.name, bossName: cpEdit.owner, businessNo: String(cpEdit.businessNo).replace(/\D/g, ''), phone: cpEdit.phone, email: cpEdit.email, address: cpEdit.address, feeRate: cpEdit.feeRate, bankName: cpEdit.bankName, bankCode: cpEdit.bankCode, accountNumber: cpEdit.accountNumber, accountHolder: cpEdit.accountHolder });
      showToast('조직 정보가 수정되었습니다.');
      setCpEditOpen(false);
      await loadCp();
      await loadPayoutProfile();
    } catch (error) { showToast(error?.message || '조직 정보를 수정하지 못했습니다.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex-1 min-h-0 overflow-hidden bg-[#f7f8fa] p-6">
      <div className="h-full min-h-0 flex gap-4">
        <section className="w-[34%] min-w-[310px] max-h-full self-start overflow-y-auto rounded-xl border border-[#e5e8eb] bg-white p-5">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-[var(--brand-primary)]">단일 운영 조직</p>
              <h1 className="mt-1.5 truncate text-[24px] font-extrabold leading-tight text-[#191f28]">{cpLoading ? '불러오는 중...' : cp?.name || '-'}</h1>
              <p className="mt-2 font-mono text-[13px] font-medium text-[#8b95a1]">{cp?.cpId || '-'}</p>
            </div>
            {cp && (
              <div className="flex shrink-0 items-center gap-2">
                <span className={`rounded-md px-2.5 py-1 text-[12px] font-bold ${cp.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {cp.status === 'ACTIVE' ? '활성' : '비활성'}
                </span>
                <button type="button" onClick={openCpEdit} className="rounded-lg border border-[#e5e8eb] p-2 text-[#4e5968] hover:bg-[#f2f4f6]" aria-label="조직 정보 수정"><Edit2 size={16} /></button>
              </div>
            )}
          </div>
          {payoutTabEnabled && (
            <div className="mb-4 grid grid-cols-2 rounded-xl bg-[#f2f4f6] p-1" role="tablist" aria-label="가맹점 정보 구분">
              <button
                type="button"
                role="tab"
                aria-selected={cpPanelTab === 'payout'}
                onClick={() => setCpPanelTab('payout')}
                className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-bold transition ${cpPanelTab === 'payout' ? 'bg-white text-[#191f28] shadow-sm' : 'text-[#6b7684] hover:text-[#333d4b]'}`}
              >
                지급·AML
                <span className={`h-2 w-2 rounded-full ${payoutProfile?.payoutEligible ? 'bg-emerald-500' : 'bg-amber-400'}`} aria-hidden="true" />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={cpPanelTab === 'basic'}
                onClick={() => setCpPanelTab('basic')}
                className={`rounded-lg px-3 py-2.5 text-[13px] font-bold transition ${cpPanelTab === 'basic' ? 'bg-white text-[#191f28] shadow-sm' : 'text-[#6b7684] hover:text-[#333d4b]'}`}
              >
                기본정보
              </button>
            </div>
          )}
          {cp && (!payoutTabEnabled || cpPanelTab === 'basic') && (
            <div className="grid grid-cols-2 gap-x-6 border-t border-[#f0f2f1]">
              <Detail label="대표자명" value={cp.owner} />
              <Detail label="사업자번호" value={formatBusinessNo(cp.businessNo)} mono />
              <Detail label="연락처" value={formatPhone(cp.phone)} mono />
              <Detail label="이메일" value={cp.email} />
              <Detail label="수수료율" value={cp.feeRate === '' ? '-' : `${cp.feeRate}%`} />
              <Detail label="주소" value={cp.address} wide />
              <Detail label="정산계좌" value={[cp.bankName, cp.accountNumber, cp.accountHolder].filter(Boolean).join(' · ')} wide />
            </div>
          )}
          {payoutTabEnabled && cpPanelTab === 'payout' && <SingleCpPayoutPanel compact capability={payoutCapability} profile={payoutProfile} loading={payoutLoading} error={payoutError} onReload={loadPayoutProfile} />}
          {payoutTabEnabled && cpPanelTab === 'payout' && payoutLoading && <p className="py-8 text-center text-[12px] text-[#8b95a1]">KG이니시스 지급 상태 확인 중...</p>}
          {ADMIN_CP_PAYOUT_TENANT_ENABLED && !payoutTabEnabled && payoutError && <p className="mt-4 rounded-lg bg-red-50 p-3 text-[11px] text-red-700">{payoutError}</p>}
        </section>

        <section className="flex-1 min-w-0 overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
          <nav className="flex h-[52px] shrink-0 items-end gap-6 border-b border-[#e5e8eb] px-5" aria-label="관리 구분">
            <button type="button" onClick={() => setSearchParams({}, { replace: true })} className={`flex h-full items-center gap-2 border-b-2 px-1 text-[14px] font-bold ${activeTab === 'salespeople' ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-[#8b95a1] hover:text-[#4e5968]'}`}><Users size={17} />영업자 관리</button>
            <button type="button" onClick={() => setSearchParams({ tab: 'pharmacies' }, { replace: true })} className={`flex h-full items-center gap-2 border-b-2 px-1 text-[14px] font-bold ${activeTab === 'pharmacies' ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-[#8b95a1] hover:text-[#4e5968]'}`}><Building2 size={17} />거래처 관리</button>
          </nav>
          {activeTab === 'salespeople' ? <>
          <header className="shrink-0 border-b border-[#eef1f4] px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-[18px] font-bold text-[#191f28]">영업자 관리</h2>
                <p className="mt-1 text-[12px] text-[#8b95a1]">총 {terminals.length.toLocaleString('ko-KR')}명</p>
              </div>
              <span className="text-[12px] font-semibold text-[#6b7684]">{terminalRegistrationNotice}</span>
            </div>
            <div className="mt-3 flex h-[40px] w-[360px] max-w-full items-center gap-2 rounded-lg border border-[#e5e8eb] px-3 focus-within:border-[var(--brand-primary)]">
              <Search size={16} className="text-[#8b95a1]" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="코드, ID, 이름, 연락처 검색" className="min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none" />
            </div>
          </header>
          {terminalError && <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">{terminalError}</div>}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <table className="w-full table-fixed text-left text-[12px]">
              <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]">
                <tr className="h-[40px] border-b border-[#e5e8eb]"><th className="w-[11%] px-3">코드</th><th className="w-[20%] px-3">로그인 ID</th><th className="w-[15%] px-3">이름</th><th className="w-[20%] px-3">연락처</th><th className="w-[12%] px-3 text-right">담당 거래처</th><th className="w-[10%] px-3 text-center">상태</th><th className="w-[12%] px-3 text-center">관리</th></tr>
              </thead>
              <tbody className="divide-y divide-[#f0f2f1]">
                {terminalLoading ? <tr><td colSpan={7} className="py-14 text-center text-[#8b95a1]">영업자를 불러오는 중입니다.</td></tr> : filtered.length === 0 ? <tr><td colSpan={7} className="py-14 text-center text-[#8b95a1]">등록된 영업자가 없습니다.</td></tr> : filtered.map((row) => (
                  <tr key={row.id} className={row.status === 'ACTIVE' ? 'h-[48px] hover:bg-[#f9fafb]' : 'h-[48px] bg-[#fafafa] text-[#8b95a1]'}>
                    <td className="truncate px-3 font-mono">{row.code}</td><td className="truncate px-3 font-mono">{row.terminalId || '-'}</td><td className="truncate px-3 font-semibold text-[#191f28]">{row.name || '-'}</td><td className="truncate px-3">{formatPhone(row.phone)}</td><td className="px-3 text-right tabular-nums">{row.customerCount.toLocaleString('ko-KR')}곳</td>
                    <td className="px-3 text-center"><button type="button" onClick={() => toggleStatus(row)} className={`rounded px-2 py-1 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{row.status === 'ACTIVE' ? '활성' : '비활성'}</button></td>
                    <td className="px-3"><div className="flex justify-center gap-1"><button type="button" onClick={() => openEdit(row)} className="rounded border border-[#e5e8eb] p-1.5 text-[var(--brand-primary)]" aria-label="영업자 수정"><Edit2 size={14} /></button><button type="button" onClick={() => { setPasswordTarget(row); setNewPassword(''); }} className="rounded border border-[#e5e8eb] p-1.5 text-[#4e5968]" aria-label="비밀번호 변경"><KeyRound size={14} /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </> : <SingleCpPharmacyManage embedded />}
        </section>
      </div>

      {terminalModal && <Modal title="영업자 정보 수정" onClose={() => setTerminalModal(null)} footer={<><button type="button" onClick={() => setTerminalModal(null)} className="flex-1 rounded-lg bg-[#e5e8eb] py-3 font-bold text-[#4e5968]">취소</button><button type="button" disabled={saving} onClick={saveTerminal} className="flex-1 rounded-lg bg-[var(--brand-primary)] py-3 font-bold text-white disabled:opacity-50">저장</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[12px] text-[#6b7684]">로그인 ID<input disabled value={terminalForm.terminalId} onChange={(e) => setTerminalForm((p) => ({ ...p, terminalId: e.target.value }))} className={`${inputClass} mt-1 disabled:bg-[#f7f8fa] disabled:text-[#8b95a1]`} /></label>
          <label className="text-[12px] text-[#6b7684]">이름<input value={terminalForm.name} onChange={(e) => setTerminalForm((p) => ({ ...p, name: e.target.value }))} className={`${inputClass} mt-1`} /></label>
          <label className="text-[12px] text-[#6b7684]">연락처<input value={terminalForm.phone} onChange={(e) => setTerminalForm((p) => ({ ...p, phone: e.target.value }))} className={`${inputClass} mt-1`} /></label>
          <label className="col-span-2 text-[12px] text-[#6b7684]">메모<input value={terminalForm.memo} onChange={(e) => setTerminalForm((p) => ({ ...p, memo: e.target.value }))} className={`${inputClass} mt-1`} /></label>
        </div>
      </Modal>}

      {passwordTarget && <Modal title={`${passwordTarget.name} 비밀번호 변경`} onClose={() => setPasswordTarget(null)} footer={<><button type="button" onClick={() => setPasswordTarget(null)} className="flex-1 rounded-lg bg-[#e5e8eb] py-3 font-bold text-[#4e5968]">취소</button><button type="button" disabled={saving} onClick={savePassword} className="flex-1 rounded-lg bg-[var(--brand-primary)] py-3 font-bold text-white disabled:opacity-50">변경</button></>}><label className="text-[12px] text-[#6b7684]">새 비밀번호<input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={`${inputClass} mt-1`} /></label></Modal>}

      {cpEditOpen && <Modal title="조직 정보 수정" onClose={() => setCpEditOpen(false)} footer={<><button type="button" onClick={() => setCpEditOpen(false)} className="flex-1 rounded-lg bg-[#e5e8eb] py-3 font-bold text-[#4e5968]">취소</button><button type="button" disabled={saving} onClick={saveCp} className="flex-1 rounded-lg bg-[var(--brand-primary)] py-3 font-bold text-white disabled:opacity-50">저장</button></>}>
        <div className="grid grid-cols-2 gap-3">{[['name','조직명'],['owner','대표자명'],['businessNo','사업자번호'],['phone','연락처'],['email','이메일'],['feeRate','수수료율'],['accountNumber','계좌번호'],['accountHolder','예금주']].map(([key,label]) => <label key={key} className="text-[12px] text-[#6b7684]">{label}<input value={cpEdit[key] ?? ''} onChange={(e) => setCpEdit((p) => ({ ...p, [key]: e.target.value }))} className={`${inputClass} mt-1`} /></label>)}<div className="col-span-2 text-[12px] text-[#6b7684]">은행<Select options={kgInicisBankOptions} value={kgInicisBankOptions.find((option) => option.value === cpEdit.bankCode) ?? null} onChange={(option) => setCpEdit((previous) => ({ ...previous, bankCode: option?.value ?? '', bankName: option?.label ?? '' }))} isSearchable isClearable placeholder="은행 검색 또는 선택" noOptionsMessage={() => '검색 결과 없음'} styles={bankSelectStyles} classNamePrefix="kg-bank-select" menuPortalTarget={typeof document !== 'undefined' ? document.body : null} menuPosition="fixed" className="mt-1" /></div><label className="col-span-2 text-[12px] text-[#6b7684]">주소<input value={cpEdit.address ?? ''} onChange={(e) => setCpEdit((p) => ({ ...p, address: e.target.value }))} className={`${inputClass} mt-1`} /></label></div>
      </Modal>}
    </div>
  );
}
